// Email two-step verification (Owais, 27 Oct 2026): shapes and small pure helpers shared by the sign-in page and
// Profile → Security. Codes are typed by the customer and sent once; they are never stored or logged here.

/**
 * OneGate's answer when a password was right but a second step is still needed (HTTP 202). method 'email' (or absent, older
 * servers): a code was emailed (maskedEmail, sent, resend). method 'authenticator' (Owais, 30 Oct 2026): a code from the
 * authenticator app or a recovery code; nothing is emailed and there is no resend.
 */
export interface TwoStepChallenge {
  status: 'two_step_required';
  method?: 'email' | 'authenticator';
  challengeId: string;
  maskedEmail?: string;
  sent?: boolean;
  recoveryAllowed?: boolean;
  expiresIn?: number;
  resendIn?: number;
  sendsLeft?: number;
  message?: string;
}

export interface TwoStepStatus {
  enabled: boolean;
  enabledAt: string | null;
  email: string;
  maskedEmail: string;
  emailVerified: boolean;
  hasPassword: boolean;
}

export function isTwoStepChallenge(value: unknown): value is TwoStepChallenge {
  const v = value as TwoStepChallenge | null;
  return !!v && v.status === 'two_step_required' && typeof v.challengeId === 'string' && /^[0-9a-f]{64}$/.test(v.challengeId);
}

export function isAuthenticatorChallenge(value: unknown): boolean {
  return isTwoStepChallenge(value) && (value as TwoStepChallenge).method === 'authenticator';
}

/** Profile → Security → Authenticator app (Owais, 30 Oct 2026). */
export interface AuthenticatorStatus { enabled: boolean; createdAt: string | null; recoveryCodesLeft: number; hasPassword?: boolean; }
/** Shown once while setting up: the QR image and the same key for typing in by hand. */
export interface AuthenticatorSetup { setupId: string; qrDataUrl: string; manualKey: string; issuer: string; account: string; expiresIn?: number; replace?: boolean; }

/** Recovery code as typed → "abcd-efgh-jkmn" (letters and digits only, at most 12, grouped by 4). */
export function cleanRecoveryCode(value: unknown): string {
  const v = String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12);
  return v.replace(/(.{4})(?=.)/g, '$1-');
}

/** The text saved by Copy / Download for recovery codes. */
export function recoveryCodesText(codes: string[], account = ''): string {
  return ['RingOffice recovery codes' + (account ? ` (${account})` : ''), 'Each code works once. Keep them somewhere safe.', '', ...codes, ''].join('\n');
}

/** Digits only, at most 6 (paste of "123 456" works). */
export function cleanCode(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '').slice(0, 6);
}

/** Email change with the authenticator app on (round 48): the typed code → {code} or {recoveryCode}, or null when incomplete. */
export function emailChangeFactor(value: unknown, useRecovery: boolean): { code?: string; recoveryCode?: string } | null {
  if (useRecovery) { const r = cleanRecoveryCode(value); return r.length === 14 ? { recoveryCode: r } : null; }
  const c = cleanCode(value);
  return c.length === 6 ? { code: c } : null;
}

/** Error codes after which the customer must start again (password step / new request). */
export function challengeEnded(code: unknown): boolean {
  return ['ChallengeNotFound', 'CodeUsed', 'TooManyAttempts', 'TooManyCodes'].includes(String(code || ''));
}

/** Plain text for an API error from the two-step endpoints (server wording first). */
export function twoStepErrorText(error: any, fallback = 'That code could not be verified. Try again.'): string {
  const e = error?.error || {};
  if (typeof e.message === 'string' && e.message) return e.message;
  if (error?.status === 0) return 'We could not reach RingOffice. Check your connection and try again.';
  return fallback;
}
